import { Component, Input, OnChanges, OnDestroy } from '@angular/core';

import { Observable, Subscription, map, of } from 'rxjs';

import { CustomInsights } from '../../../Monitor/Shared/models/custom-insights.model';
import { CoverageAnalysisService } from '../../../Monitor/Shared/services/coverage-analysis.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import { CurationAgentActionModel } from '../../models/curation-agent-action.model';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { CurationAgentsService } from '../../services/curation-agents.service';

/**
 * What the agent is and what it does, shown in the mention feed's right hand panel. Plays the same
 * part for an agent that the summary tab does for a topic or folder.
 */
@Component({
    selector: 'agpr-agent-summary',
    templateUrl: './agent-summary.component.html',
    styleUrls: ['./agent-summary.component.scss'],
    standalone: false
})
export class AgentSummaryComponent implements OnChanges, OnDestroy {
    @Input() agent: CurationAgentModel;

    createdBy$: Observable<string>;
    lastModifiedBy$: Observable<string>;
    /** Dates are shown in the viewer's own timezone */
    readonly userTimeZone$: Observable<string> = this.curationAgentsService.userTimeZone$;

    /** The prompt the agent evaluates mentions against, once it has been resolved by id */
    prompt: CustomInsights;

    private readonly subscriptions: Subscription[] = [];

    constructor(
        private usersService: UsersService,
        private coverageAnalysisService: CoverageAnalysisService,
        private curationAgentsService: CurationAgentsService
    ) {}

    ngOnChanges(): void {
        this.createdBy$ = this.userName(this.agent?.createdByUserId);
        this.lastModifiedBy$ = this.userName(this.agent?.lastModifiedByUserId);

        this.subscriptions.push(
            this.coverageAnalysisService.customInsights$.pipe(
                map(prompts => prompts.find(item => item.id === this.agent?.customPromptId))
            ).subscribe(prompt => this.prompt = prompt)
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /** Everything the agent does to a mention it keeps. Negative actions are listed after them. */
    get actions(): CurationAgentActionModel[] {
        return [...(this.agent?.positiveActions ?? []), ...(this.agent?.negativeActions ?? [])];
    }

    private userName(id: number): Observable<string> {
        return id ? this.usersService.getUserNameByID(id) : of(null);
    }
}
